import { ReactNode, useCallback } from "react";
import { RouterLink } from "@/components/shared/link/Link";
import { IceCubesIcon } from "@/v2/assets/icons/IceCubesIcon";
import { UserIcon } from "@/v2/assets/icons/UserIcon";
import { DatabaseSync01 } from "@/v2/assets/icons/DatabaseSync01";
import { UserSquare } from "@/v2/assets/icons/UserSquare";
import { Building03Icon } from "@/v2/assets/icons/Building03Icon";
import { CreditCardIcon } from "@/v2/assets/icons/CreditCardIcon";
import { Layers02Icon } from "@/v2/assets/icons/Layers02Icon";
import { LayoutListIcon } from "@/v2/assets/icons/LayoutListIcon";
import { Route01 } from "@/v2/assets/icons/Route01";
import { Notifications01 } from "@/v2/assets/icons/Notifications01";
import { TimeSchedule } from "@/v2/assets/icons/TimeSchedule";
import { ApiKeyIcon } from "@/v2/assets/icons/ApiKeyIcon";
import { File02Icon } from "@/v2/assets/icons/File02Icon";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { Button, Flex, Separator, Text } from "@radix-ui/themes";
import styles from "./SettingsPage.module.scss";
import { cn } from "@/lib/utils";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

interface SettingsSidebarMenuItem {
  id: string;
  label: string;
  icon: ReactNode;
  path: string;
}

function SidebarMenuItem({ item }: { item: SettingsSidebarMenuItem }) {
  const active = useMatch({
    path: item.path,
    end: true,
  });

  return (
    <RouterLink
      to={item.path}
      variant={"soft"}
      size="2"
      color="gray"
      replace
      className={cn(styles.item, !active && styles.notActive)}
    >
      {item.icon}
      {item.label}
    </RouterLink>
  );
}

const settingsSidebarMenuItems: SettingsSidebarMenuItem[] = [
  {
    id: "data_sources",
    label: "Data Sources",
    icon: <DatabaseSync01 fill="gray" />,
    path: "/settings/data-sources",
  },
  {
    id: "members",
    label: "Members",
    icon: <UserSquare fill="gray" />,
    path: "/settings/members",
  },
  {
    id: "agents",
    label: "Agents",
    icon: <UserIcon fill="gray" />,
    path: "/settings/agents",
  },
  {
    id: "topics",
    label: "Topics",
    icon: <IceCubesIcon fill="gray" />,
    path: "/settings/topics",
  },
  {
    id: "subtopics",
    label: "Sub topics",
    icon: <LayoutListIcon fill="gray" />,
    path: "/settings/subtopics",
  },
  {
    id: "brands",
    label: "Brands",
    icon: <Building03Icon fill="gray" />,
    path: "/settings/brands",
  },
  {
    id: "custom_fields",
    label: "Custom Fields",
    icon: <Layers02Icon fill="gray" />,
    path: "/settings/custom-fields",
  },
  {
    id: "field_sync",
    label: "2-Side Sync",
    icon: <Route01 fill="gray" />,
    path: "/settings/field-sync",
  },
  {
    id: "api_keys",
    label: "API Keys",
    icon: <ApiKeyIcon fill="gray" />,
    path: "/settings/api-keys",
  },
  {
    id: "billing",
    label: "Billing & Invoicing",
    icon: <CreditCardIcon fill="gray" />,
    path: "/settings/billing",
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: <Notifications01 fill="gray" />,
    path: "/settings/notifications",
  },
  {
    id: "business_hours",
    label: "Business Hours",
    icon: <TimeSchedule fill="gray" />,
    path: "/settings/business-hours",
  },
  {
    id: "gdpr",
    label: "GDPR",
    icon: <File02Icon fill="gray" />,
    path: "/settings/gdpr",
  },
  {
    id: "auto_response",
    label: "Auto-Response",
    icon: <TimeSchedule fill="gray" />,
    path: "/settings/auto-response",
  },
];

export function SettingsPageSidebar() {
  const navigate = useNavigate();
  const billingEnabled = useFlag("billingEnabled", false);
  const isGdprSettingsEnabled = useFlag("gdpr-setting-page", false);
  const handleBackToWorkspace = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Filter workspace items based on feature flag
  const workspaceItems = ["members", "api_keys"];

  workspaceItems.push("notifications");

  if (billingEnabled) {
    workspaceItems.push("billing");
  }

  return (
    <Flex direction="column" gap="4" className={styles.sidebar}>
      <Button
        variant="soft"
        size="2"
        color="gray"
        onClick={handleBackToWorkspace}
        className="justify-start"
      >
        <ArrowLeftIcon />
        Back to Workspace
      </Button>

      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text as="label" size="1" className="pb-2">
            Data
          </Text>

          {settingsSidebarMenuItems
            .filter((item) => {
              const dataItems = [
                "data_sources",
                "agents",
                "topics",
                "subtopics",
                "brands",
                "custom_fields",
                "field_sync",
                "business_hours",
                "auto_response",
                ...(isGdprSettingsEnabled ? ["gdpr"] : []),
              ];
              return dataItems.includes(item.id);
            })
            .map((item) => (
              <SidebarMenuItem key={item.id} item={item} />
            ))}
        </Flex>

        <Separator orientation="horizontal" size="4" />

        <Flex direction="column" gap="1">
          <Text as="label" size="1" className="pb-2">
            Workspace
          </Text>

          {settingsSidebarMenuItems
            .filter((item) => {
              if (item.id === "billing" && !billingEnabled) {
                return false;
              }
              return ["notifications", "members", "billing", "api_keys"].includes(item.id);
            })
            .filter((item) => workspaceItems.includes(item.id))
            .map((item) => (
              <SidebarMenuItem key={item.id} item={item} />
            ))}
        </Flex>
      </Flex>
    </Flex>
  );
}
